import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CreateTeamInput, UpdateTeamInput } from '@this+that/models';
import log from 'loglevel';

import { useCustomToast } from '../../../../hooks/useCustomToast';
import { APP_PAGES, buildTeamDetailPath } from '../../../../routes/constants';
import {
  useCreateConsultingTeam,
  useCreateTeam,
  useDeleteTeam,
  useRemoveMember,
  useUpdateTeam,
} from '../../../../services/team.service';
import { useTeamStore } from '../../../../store/Team/useTeamStore';

export function useTeamActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { successMessage, errorMessage } = useCustomToast();

  const { createTeam: createTeamMutation, loading: createLoading } =
    useCreateTeam();
  const {
    createConsultingTeam: createConsultingTeamMutation,
    loading: createConsultingTeamLoading,
  } = useCreateConsultingTeam();
  const { updateTeam: updateTeamMutation, loading: updateLoading } =
    useUpdateTeam();
  const { deleteTeam: deleteTeamMutation, loading: deleteLoading } =
    useDeleteTeam();
  const { removeMember, loading: removeLoading } = useRemoveMember();

  const createTeam = async (team: CreateTeamInput): Promise<boolean> => {
    try {
      const newTeam = await createTeamMutation(team);
      if (!newTeam?.id) {
        return false;
      }
      const store = useTeamStore.getState();
      store.setTeamData(newTeam.id, newTeam, [newTeam]);
      store.addUserTeamId(newTeam.id);
      successMessage(t('teamHub.toasts.created'));
      navigate(buildTeamDetailPath(newTeam.id));
      return true;
    } catch (err) {
      log.error('useTeamActions.createTeam', err);
      errorMessage(t('teamHub.toasts.errorCreate'));
      return false;
    }
  };

  const createConsultingTeam = async (
    team: CreateTeamInput,
  ): Promise<boolean> => {
    try {
      const newTeam = await createConsultingTeamMutation(team);
      if (!newTeam?.id) {
        return false;
      }
      const store = useTeamStore.getState();
      store.setTeamData(newTeam.id, newTeam, [newTeam]);
      store.addUserTeamId(newTeam.id);
      successMessage(t('teamHub.toasts.consultingCreated'));
      navigate(buildTeamDetailPath(newTeam.id));
      return true;
    } catch (err) {
      log.error('useTeamActions.createConsultingTeam', err);
      errorMessage(t('teamHub.toasts.errorCreate'));
      return false;
    }
  };

  const updateTeam = async (team: UpdateTeamInput): Promise<boolean> => {
    try {
      await updateTeamMutation(team);
      useTeamStore.getState().patchTeam(team.id, { ...team });
      successMessage(t('teamHub.toasts.updated'));
      return true;
    } catch (err) {
      log.error('useTeamActions.updateTeam', err);
      errorMessage(t('teamHub.toasts.errorUpdate'));
      return false;
    }
  };

  const deleteTeam = async (id: string) => {
    try {
      await deleteTeamMutation(id);
      useTeamStore.getState().removeTeam(id);
      successMessage(t('teamHub.toasts.deleted'));
      navigate(APP_PAGES.TEAM);
    } catch (err) {
      log.error('useTeamActions.deleteTeam', err);
      errorMessage(t('teamHub.toasts.errorDelete'));
    }
  };

  const leaveTeam = async (id: string, userId: string) => {
    try {
      await removeMember(id, userId);
      useTeamStore.getState().removeTeam(id);
      successMessage(t('teamHub.toasts.left'));
      navigate(APP_PAGES.TEAM);
    } catch (err) {
      log.error('useTeamActions.leaveTeam', err);
      errorMessage(t('teamHub.toasts.errorLeave'));
    }
  };

  return {
    createTeam,
    createConsultingTeam,
    updateTeam,
    deleteTeam,
    leaveTeam,
    createLoading,
    createConsultingTeamLoading,
    updateLoading,
    deleteLoading,
    removeLoading,
  };
}
